import ClearIcon from "@mui/icons-material/Clear";

import CustomSelect from "@components/Forms/FormInputs/CustomSelect";

import { ICompanyFilters } from "@interfaces/filter.type";

import { ICompanyTableControls } from "@store/filters/filter.types";

type AssociationKeysType = keyof Pick<
  ICompanyFilters,
  "autm2023" | "autm2024" | "bio2023" | "bio2024" | "les2023" | "uidp2023"
>;

const associationDescriptions = {
  autm2023: "AUTM 2023",
  autm2024: "AUTM 2024",
  bio2023: "BIO 2023",
  bio2024: "BIO 2024",
  les2023: "LES 2023",
  uidp2023: "UIDP 2023",
};

const associations = Object.keys(associationDescriptions) as AssociationKeysType[];

interface IAssociationSelectProperties {
  filterState: ICompanyTableControls["filters"];
  onChange: (newState: ICompanyTableControls["filters"]) => void;
}

/**
 * This component provides a select input for the user to filter by associations.
 *
 * @param filterState - The current state of the filter.
 * @param handleFilterChange - Function to trigger on change event.
 */
function AssociationSelect({ filterState, onChange }: IAssociationSelectProperties) {
  const associationOptions = Object.entries(associationDescriptions).map(([key, description]) => ({
    description,
    key,
  }));

  const selectedValues = associations.filter((association) => filterState[association]);

  const handleChange = (newValues: string[]) => {
    const newState = { ...filterState };
    for (const association of associations) {
      newState[association] = newValues.includes(association);
    }
    onChange(newState);
  };

  const renderValue = (selected: AssociationKeysType[]) => {
    const firstAssociation = selected[0];
    if (firstAssociation) {
      const description = associationDescriptions[firstAssociation];
      const extraCount = selected.length - 1;
      return extraCount > 0 ? `${description} and ${extraCount} more` : description;
    }
    return "";
  };

  return (
    <CustomSelect<(typeof associationOptions)[number]>
      clearIcon={<ClearIcon />}
      customRenderValue={renderValue as (selected: string[]) => string}
      defaultSelectedOptions={[]}
      formControlSx={{ width: "100%" }}
      getOptionLabel={(option) => option.description}
      getOptionValue={(option) => option.key}
      inputLabel="Select Associations"
      isMultiSelect={true}
      onChange={handleChange}
      options={associationOptions}
      selectedValues={selectedValues}
    />
  );
}

export default AssociationSelect;
